<template>
    <div :class="wrapperClass">
        <!-- Redesign: a writing surface you see, not a grey strip — bordered,
             white, lifted a little, a brand ring on focus. One row to start
             (MYAC-1522, Aymeric's review): the paperclip, the first line of text
             and the send button share one centre line; as the text grows, the
             two buttons stay on its last line (items-end). -->
        <div class="chat-composer flex flex-row items-end justify-center rounded-2xl border border-surface-300 bg-white p-1.5">
            <!-- MYAC-1182 — a disabled PrimeVue button swallows its own tooltip
                 (frontend.md), so the reason lives on this wrapper span. Visible
                 and disabled rather than hidden (`attachDisabled`, e.g. no project
                 open yet) — same "disabled over hidden" rule as everywhere else. -->
            <span v-tooltip.top="attachTooltip" class="inline-flex">
                <Button icon="pi pi-paperclip" text rounded size="small"
                    :aria-label="t('chat.attachments.attach-aria')"
                    :disabled="isLoading || isStreaming || disabled || attachDisabled"
                    class="m-1" @click="triggerFilePicker" />
            </span>
            <input ref="fileInputRef" type="file" multiple :accept="ATTACHMENT_ACCEPT" class="hidden"
                @change="onFileInputChange" />

            <!-- MYAC-1151 — no `size="large"`. The control typed into most often
                 in the app was the one running a size bigger than the 14px
                 compact base everything else uses (the `front-ui` skill). Appearance
                 only: autoResize, the 30vh cap and the Enter/Shift+Enter
                 handling below are untouched. -->
            <Textarea ref="textareaRef" v-model="localInput" :placeholder="placeholder || t('chat.type-message')"
                class="chat-textarea flex-1 bg-transparent! border-none! shadow-none! text-base!" :rows="1" :autoResize="true"
                @keydown.enter="handleKeyDown" />

            <!-- MYAC-1519 / MYAC-1609 — Stop the answer being written, beside Send and
                 drawn like it (filled, round, same size): one family of controls in
                 the box. Disabled with its reason when nothing runs; the reason sits
                 on the wrapper because a disabled button swallows its own tooltip. -->
            <span v-if="stop" class="inline-flex"
                v-tooltip.top="stop.enabled ? t('chat.command.stop') : t('chat.command.stop_idle')">
                <Button icon="pi pi-stop" rounded size="small" class="m-1"
                    data-testid="chat-stop" :aria-label="t('chat.command.stop')"
                    :disabled="!stop.enabled" @click="emit('stop')" />
            </span>
            <!-- `small` so the row's height follows the textarea rather than the
                 button. Every disabled/loading condition is unchanged. -->
            <Button icon="pi pi-arrow-up" rounded size="small" :loading="isLoading || isStreaming"
                :disabled="!localInput.trim() || isLoading || isStreaming || disabled || sendBlocked" @click="handleSend" class="m-1" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import { useI18n } from 'vue-i18n'
import { ATTACHMENT_ACCEPT } from '@/stores/chatAttachments'

interface Props {
    modelValue: string
    isLoading?: boolean
    isStreaming?: boolean
    disabled?: boolean
    wrapperClass?: string
    /** Optional placeholder override (e.g. context-specific example prompts).
     *  Falls back to the generic `chat.type-message` when unset. */
    placeholder?: string
    /** MYAC-1182 — a reason the paperclip is temporarily unusable beyond the
     *  loading/streaming/disabled states above (e.g. no project open yet).
     *  Shown as the button's tooltip in place of the generic aria-label. */
    attachDisabled?: boolean
    attachDisabledReason?: string
    /** MYAC-1182 — uploads in flight: only SENDING waits for them. Typing and
     *  attaching more files stay live (uploads run in parallel); wiring this
     *  into `disabled` instead froze the whole input per file — live finding,
     *  2026-09-09. */
    sendBlocked?: boolean
    /** MYAC-1519 — the chat can stop an answer: Stop shows beside Send, pressable
     *  while `enabled`. Null where there is nothing to stop (no project chat). */
    stop?: { enabled: boolean } | null
}

const props = withDefaults(defineProps<Props>(), {
    isLoading: false,
    isStreaming: false,
    disabled: false,
    wrapperClass: 'p-4',
    attachDisabled: false,
    attachDisabledReason: '',
    sendBlocked: false,
    stop: null,
})

const emit = defineEmits<{
    'update:modelValue': [value: string]
    'send': [message: string]
    /** MYAC-1182 — this component stays a dumb v-model input: the parent (which
     *  owns the chatbot/attachments stores) validates, uploads and renders the
     *  chip row. Mirrors how `send` hands the typed text up rather than posting
     *  it itself. */
    'attach-files': [files: File[]]
    'stop': []
}>()

const { t } = useI18n()
const localInput = ref(props.modelValue)
const fileInputRef = ref<HTMLInputElement | null>(null)
const textareaRef = ref<{ $el: HTMLTextAreaElement } | null>(null)

const attachTooltip = computed(() =>
    props.attachDisabled && props.attachDisabledReason
        ? props.attachDisabledReason
        : t('chat.attachments.attach-aria'))

watch(() => props.modelValue, (newValue) => {
    localInput.value = newValue
})

watch(localInput, (newValue) => {
    emit('update:modelValue', newValue)
    if (newValue === '') collapseToOneLine()
})

// MYAC-1388 — PrimeVue's autoResize pins an inline height and only shrinks
// when scrollHeight falls below it, which an emptied textarea never does: the
// box kept its composing height after a send. Dropping the inline height once
// the text is gone (sent or cleared) lets `rows=1` take over again.
function collapseToOneLine() {
    nextTick(() => {
        const el = textareaRef.value?.$el
        if (el) el.style.height = ''
    })
}

const handleKeyDown = (event: KeyboardEvent) => {
    // Enter sends the message, Shift+Enter creates a new line
    // When streaming, Enter creates a new line (don't send)
    if (event.key === 'Enter' && !event.shiftKey && !props.isStreaming && !props.disabled
        && !props.sendBlocked) {
        event.preventDefault()
        handleSend()
    }
    // Shift+Enter or streaming: allow default behavior (new line)
}

const handleSend = () => {
    if (!localInput.value.trim() || props.isLoading || props.isStreaming || props.disabled
        || props.sendBlocked) {
        return
    }
    const message = localInput.value.trim()
    emit('send', message)
}

const triggerFilePicker = () => {
    if (props.isLoading || props.isStreaming || props.disabled || props.attachDisabled) return
    fileInputRef.value?.click()
}

const onFileInputChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    const files = input.files ? Array.from(input.files) : []
    // Reset so picking the exact same file again still fires a change event.
    input.value = ''
    if (files.length) emit('attach-files', files)
}
</script>

<style scoped>
.chat-composer {
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 30px rgba(56, 0, 153, 0.06);
    transition: border-color 0.2s, box-shadow 0.2s;
}
.chat-composer:focus-within {
    border-color: var(--p-primary-color);
    box-shadow: 0 0 0 3px rgba(56, 0, 153, 0.15);
}
.chat-textarea {
    max-height: 30vh;
    field-sizing: content;
    overflow-y: auto;
    /* Stay within the flex row: let the flex item shrink and keep width bounded, so a long
       line wraps to the next line instead of expanding the box and breaking the layout. */
    width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
}
</style>
